import { useQuery } from '@tanstack/react-query';
import {
  Title,
  SimpleGrid,
  Card,
  Text,
  Group,
  ThemeIcon,
  Stack,
  Loader,
  Center,
  Alert,
  Button,
} from '@mantine/core';
import {
  IconUsers,
  IconMessage,
  IconPackage,
  IconFlag,
  IconUserPlus,
  IconFileText,
  IconAlertCircle,
  IconExternalLink,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../../services/admin';
import { API_BASE_URL } from '../../config';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  to?: string;
}

function StatCard({ title, value, icon, color, to }: StatCardProps) {
  const navigate = useNavigate();

  return (
    <Card
      shadow="sm"
      padding="lg"
      radius="md"
      withBorder
      style={to ? { cursor: 'pointer', transition: 'transform 100ms ease, box-shadow 100ms ease' } : undefined}
      onClick={to ? () => navigate(to) : undefined}
      onMouseEnter={to ? (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)'; } : undefined}
      onMouseLeave={to ? (e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; } : undefined}
    >
      <Group justify="space-between">
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          <Text size="xl" fw={700}>
            {value.toLocaleString()}
          </Text>
        </Stack>
        <ThemeIcon size={56} radius="md" variant="light" color={color}>
          {icon}
        </ThemeIcon>
      </Group>
    </Card>
  );
}

export default function Dashboard() {
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: adminService.getDashboardStats,
  });

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
        Failed to load dashboard statistics. Please try again later.
      </Alert>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: <IconUsers size={28} />,
      color: 'blue',
      to: '/admin/users',
    },
    {
      title: 'Total Reviews',
      value: stats?.totalReviews || 0,
      icon: <IconMessage size={28} />,
      color: 'green',
      to: '/admin/reviews',
    },
    {
      title: 'Total Products',
      value: stats?.totalProducts || 0,
      icon: <IconPackage size={28} />,
      color: 'violet',
      to: '/admin/products',
    },
    {
      title: 'Pending Reports',
      value: stats?.pendingReports || 0,
      icon: <IconFlag size={28} />,
      color: 'red',
      to: '/admin/moderation',
    },
    {
      title: 'New Users Today',
      value: stats?.newUsersToday || 0,
      icon: <IconUserPlus size={28} />,
      color: 'cyan',
      to: '/admin/users',
    },
    {
      title: 'New Reviews Today',
      value: stats?.newReviewsToday || 0,
      icon: <IconFileText size={28} />,
      color: 'orange',
      to: '/admin/reviews',
    },
  ];

  const hangfireUrl = API_BASE_URL.replace('/api', '/hangfire');

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Overview</Title>
        <Button
          component="a"
          href={hangfireUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="light"
          size="xs"
          rightSection={<IconExternalLink size={14} />}
        >
          Hangfire Dashboard
        </Button>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="lg">
        {statCards.map((stat) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            icon={stat.icon}
            color={stat.color}
            to={stat.to}
          />
        ))}
      </SimpleGrid>
    </Stack>
  );
}
